import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Download, Upload, Search, TrendingUp, PauseCircle, Info } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { formatIDR, formatDate } from '../lib/format';
import { MoneyInput } from '../components/MoneyInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

// Performa Koleksi (khusus owner): berapa kali tiap model disewa, pendapatannya,
// dan balik modalnya — dasar memutuskan pengadaan baru. Pendapatan satu setelan
// dicatat penuh ke atasan; Bawahan hanya dihitung jumlah sewanya.

export const STATUS_KOLEKSI = {
  laris: { l: 'Laris', cls: 'bg-success/10 text-success' },
  sedang: { l: 'Sedang', cls: 'bg-gold/20 text-gold-dark' },
  sepi: { l: 'Sepi', cls: 'bg-warning/15 text-[#8a5f1e]' },
  belum_pernah: { l: 'Belum pernah', cls: 'bg-danger/10 text-danger' },
};

export const kaliModal = (x) => (x === null || x === undefined ? '—' : `${x.toLocaleString('id-ID', { maximumFractionDigits: 2 })}×`);

const URUT = [
  ['pcs', 'Paling sering disewa'],
  ['per_stok', 'Disewa per stok'],
  ['pendapatan', 'Pendapatan terbesar'],
  ['balik_modal', 'Balik modal terbesar'],
  ['sepi', 'Paling lama tidak disewa'],
  ['modal_total', 'Modal terbesar'],
];

function Stat({ testid, label, value, sub, tone = 'text-brown-dark' }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-border shadow-soft p-4">
      <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">{label}</p>
      <p className={`font-display text-xl md:text-2xl mt-1.5 tracking-tight ${tone}`}>{value}</p>
      {sub && <p className="text-[11px] text-mocha mt-1">{sub}</p>}
    </div>
  );
}

function Saran({ testid, title, icon: Icon, rows, empty, note }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-border shadow-soft p-5">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} className="text-caramel" />
        <h2 className="font-display text-lg text-ink">{title}</h2>
      </div>
      <p className="text-xs text-mocha mb-3">{note}</p>
      {rows.length === 0 ? <p className="text-sm text-mocha">{empty}</p> : (
        <ul className="divide-y divide-border">
          {rows.map((r) => (
            <li key={r.code} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`${testid}-${r.code}`}>
              <span className="min-w-0"><b className="text-brown-dark">{r.code}</b> <span className="text-mocha">{r.name}</span></span>
              <span className="shrink-0 text-xs text-mocha text-right">
                {r.pcs} pcs · stok {r.stok}
                {r.sewa_terakhir ? ` · terakhir ${formatDate(r.sewa_terakhir)}` : ' · belum pernah'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ImporDialog({ hasil, onClose, onApply, sibuk }) {
  if (!hasil) return null;
  const bisa = hasil.jumlah_salah === 0 && hasil.akan_diubah > 0;
  return (
    <Dialog open={!!hasil} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="impor-modal-dialog">
        <DialogHeader><DialogTitle>Periksa dulu sebelum disimpan</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="rounded-xl bg-ivory p-3"><p className="text-xs text-mocha">Akan diubah</p><p className="font-semibold text-brown-dark" data-testid="impor-akan-diubah">{hasil.akan_diubah} kode</p></div>
          <div className="rounded-xl bg-ivory p-3"><p className="text-xs text-mocha">Tidak berubah / kosong</p><p className="font-semibold text-brown-dark">{hasil.tidak_berubah} / {hasil.dilewati_kosong}</p></div>
        </div>
        {hasil.jumlah_salah > 0 && (
          <div className="rounded-xl border border-danger/30 bg-danger/5 p-3 text-sm" data-testid="impor-salah">
            <p className="font-semibold text-danger mb-1">{hasil.jumlah_salah} baris salah — tidak ada yang akan disimpan sampai diperbaiki:</p>
            <ul className="text-xs text-ink space-y-0.5 max-h-40 overflow-auto">
              {hasil.salah.map((s) => <li key={`${s.baris}-${s.kode}`}>Baris {s.baris} · {s.kode}: {s.alasan}</li>)}
            </ul>
          </div>
        )}
        {hasil.contoh.length > 0 && (
          <div className="text-sm">
            <p className="text-xs text-mocha mb-1">Contoh perubahan{hasil.akan_diubah > hasil.contoh.length ? ` (${hasil.contoh.length} dari ${hasil.akan_diubah})` : ''}:</p>
            <ul className="text-xs space-y-0.5 max-h-48 overflow-auto" data-testid="impor-contoh">
              {hasil.contoh.map((u) => (
                <li key={u.code} className="flex justify-between gap-2"><span><b>{u.code}</b> {u.name}</span><span className="shrink-0">{u.lama ? formatIDR(u.lama) : 'kosong'} → <b>{u.baru ? formatIDR(u.baru) : 'kosong'}</b></span></li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="rounded-xl border border-border px-4 py-2 text-sm font-semibold text-mocha">Batal</button>
          <button data-testid="impor-simpan" disabled={!bisa || sibuk} onClick={onApply}
            className="rounded-xl bg-brown text-white px-4 py-2 text-sm font-semibold disabled:opacity-50">
            Simpan {hasil.akan_diubah} perubahan
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PerformaKoleksi() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [kat, setKat] = useState('');
  const [status, setStatus] = useState('');
  const [urut, setUrut] = useState('pcs');
  const [edit, setEdit] = useState({});
  const [impor, setImpor] = useState(null);
  const [sibuk, setSibuk] = useState(false);
  const fileRef = useRef(null);
  const b64Ref = useRef('');

  const load = useCallback(() => {
    api.get('/koleksi/performa').then((r) => setData(r.data)).catch((e) => toast.error(apiError(e, 'Gagal memuat performa koleksi')));
  }, []);
  useEffect(() => { load(); }, [load]);

  const kategori = useMemo(() => (data ? [...new Set(data.rows.map((r) => r.category))] : []), [data]);

  const rows = useMemo(() => {
    if (!data) return [];
    const kata = q.trim().toLowerCase();
    const out = data.rows.filter((r) => (!kat || r.category === kat) && (!status || r.status === status)
      && (!kata || r.code.toLowerCase().includes(kata) || r.name.toLowerCase().includes(kata)));
    const nilai = (r) => {
      if (urut === 'sepi') return r.hari_sejak_sewa === null ? 1e9 : r.hari_sejak_sewa;
      if (urut === 'balik_modal') return r.balik_modal ?? -1;
      return r[urut] || 0;
    };
    return [...out].sort((a, b) => nilai(b) - nilai(a) || a.code.localeCompare(b.code));
  }, [data, q, kat, status, urut]);

  const simpanModal = async (r) => {
    const v = edit[r.code];
    if (v === undefined) return;
    const n = Number(v || 0);
    if (n === r.modal_per_pcs) { setEdit((e) => { const x = { ...e }; delete x[r.code]; return x; }); return; }
    try {
      await api.patch(`/koleksi/modal/${encodeURIComponent(r.code)}`, { purchase_price: n });
      toast.success(`Harga beli ${r.code} disimpan`);
      setEdit((e) => { const x = { ...e }; delete x[r.code]; return x; });
      load();
    } catch (e) {
      toast.error(apiError(e, 'Gagal menyimpan harga beli'));
    }
  };

  const unduh = async () => {
    const tid = toast.loading('Menyiapkan daftar modal...');
    try {
      const r = await api.get('/koleksi/modal/unduh', { responseType: 'blob' });
      const dispo = r.headers['content-disposition'] || '';
      const fname = dispo.match(/filename="?([^";]+)"?/)?.[1] || 'Daftar_Modal_Deyran.xlsx';
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success('Daftar modal terunduh. Isi kolom Harga Beli per pcs, lalu Impor.', { id: tid });
    } catch (e) {
      toast.error(apiError(e, 'Gagal mengunduh'), { id: tid });
    }
  };

  const pilihBerkas = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 3_000_000) { toast.error('Berkas lebih dari 3 MB'); return; }
    const fr = new FileReader();
    fr.onload = async () => {
      b64Ref.current = String(fr.result);
      setSibuk(true);
      try {
        const r = await api.post('/koleksi/modal/impor', { file_base64: b64Ref.current, terapkan: false });
        setImpor(r.data);
      } catch (err) {
        toast.error(apiError(err, 'Berkas tidak bisa dibaca'));
      } finally { setSibuk(false); }
    };
    fr.readAsDataURL(file);
  };

  const terapkan = async () => {
    setSibuk(true);
    try {
      const r = await api.post('/koleksi/modal/impor', { file_base64: b64Ref.current, terapkan: true });
      toast.success(`Harga beli ${r.data.akan_diubah} busana tersimpan`);
      setImpor(null);
      load();
    } catch (err) {
      toast.error(apiError(err, 'Gagal menyimpan'));
    } finally { setSibuk(false); }
  };

  if (!data) return <div className="py-20 text-center text-mocha text-sm">Memuat performa koleksi…</div>;
  const s = data.ringkasan;

  return (
    <div className="space-y-5" data-testid="performa-koleksi-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-ink tracking-tight">Performa Koleksi</h1>
          <p className="text-sm text-mocha mt-1">Model mana yang laris, mana yang diam — dasar memutuskan pengadaan baru.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="unduh-modal" onClick={unduh}
            className="inline-flex items-center gap-2 rounded-xl bg-white border border-border text-brown px-4 py-2.5 text-sm font-semibold hover:bg-ivory">
            <Download size={15} /> Unduh daftar modal
          </button>
          <button data-testid="impor-modal" disabled={sibuk} onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-xl bg-brown text-white px-4 py-2.5 text-sm font-semibold hover:bg-brown-med disabled:opacity-60">
            <Upload size={15} /> Impor modal
          </button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="impor-modal-file" onChange={pilihBerkas} />
        </div>
      </div>

      <div className="rounded-2xl border border-gold/40 bg-gold/10 px-4 py-3 text-xs text-ink flex gap-2" data-testid="umur-data">
        <Info size={15} className="shrink-0 text-gold-dark mt-0.5" />
        <span>
          Data sewa sejak <b>{data.data_mulai ? formatDate(data.data_mulai) : '—'}</b> ({data.umur_data_hari} hari).
          Model musiman (wisuda, nikah, 17-an) bisa terlihat sepi di luar musimnya — lihat juga kapan terakhir disewa.
          Pendapatan satu setelan dihitung ke atasan; Bawahan hanya jumlah sewanya.
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat testid="stat-koleksi" label="Koleksi" value={`${s.koleksi} kode`} sub={`${s.laris} laris · ${s.sedang} sedang`} />
        <Stat testid="stat-sepi" label="Sepi / belum pernah" value={`${s.sepi} / ${s.belum_pernah}`} tone="text-danger" />
        <Stat testid="stat-pendapatan" label="Pendapatan sewa" value={formatIDR(s.pendapatan_total)} sub="sejak data ada" />
        <Stat testid="stat-modal-terisi" label="Modal terisi" value={`${s.modal_terisi} / ${s.modal_perlu}`}
          sub={s.modal_terisi < s.modal_perlu ? 'isi lewat Unduh → Impor' : 'lengkap'} tone={s.modal_terisi < s.modal_perlu ? 'text-[#8a5f1e]' : 'text-success'} />
        <Stat testid="stat-balik-modal" label="Balik modal" value={kaliModal(s.balik_modal)}
          sub={s.balik_modal === null ? 'isi harga beli dulu' : `dari ${s.modal_terisi} kode yang modalnya terisi`} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Saran testid="saran-tambah" title="Layak ditambah" icon={TrendingUp} rows={data.tambah}
          note={`Disewa ≥ ${data.ambang.laris}× per pcs stok. Pertimbangkan tambah stok / model mirip.`}
          empty="Belum ada yang memenuhi syarat laris." />
        <Saran testid="saran-tahan" title="Tahan pengadaan model ini" icon={PauseCircle} rows={data.tahan}
          note="Sepi atau belum pernah disewa, stok ≥ 3. Jangan beli yang mirip dulu; coba promosikan."
          empty="Tidak ada yang perlu ditahan." />
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-soft p-5 overflow-x-auto" data-testid="per-kategori">
        <h2 className="font-display text-lg text-ink mb-3">Per kategori</h2>
        <table className="w-full text-sm min-w-[520px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-mocha">
            <th className="py-1.5">Kategori</th><th className="text-right">Kode</th><th className="text-right">Stok</th>
            <th className="text-right">Disewa</th><th className="text-right">Per stok</th><th className="text-right">Belum pernah</th><th className="text-right">Pendapatan</th>
          </tr></thead>
          <tbody>
            {data.kategori.map((k) => (
              <tr key={k.category} className="border-t border-border">
                <td className="py-1.5 font-semibold text-brown-dark">{k.category}</td>
                <td className="text-right">{k.kode}</td><td className="text-right">{k.stok}</td>
                <td className="text-right">{k.pcs} pcs</td><td className="text-right">{kaliModal(k.per_stok)}</td>
                <td className="text-right">{k.belum_pernah}</td>
                <td className="text-right">{k.category === 'Bawahan' ? 'ikut atasan' : formatIDR(k.pendapatan)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-soft p-5 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-mocha" />
            <input data-testid="cari-koleksi" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari kode / nama"
              className="w-full rounded-xl border border-border pl-8 pr-3 py-2 text-sm" />
          </div>
          <select data-testid="filter-kategori" value={kat} onChange={(e) => setKat(e.target.value)} className="rounded-xl border border-border px-3 py-2 text-sm bg-white">
            <option value="">Semua kategori</option>
            {kategori.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-border px-3 py-2 text-sm bg-white">
            <option value="">Semua status</option>
            {Object.entries(STATUS_KOLEKSI).map(([v, x]) => <option key={v} value={v}>{x.l}</option>)}
          </select>
          <select data-testid="urutkan" value={urut} onChange={(e) => setUrut(e.target.value)} className="rounded-xl border border-border px-3 py-2 text-sm bg-white">
            {URUT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <p className="text-xs text-mocha" data-testid="jumlah-baris">{rows.length} kode ditampilkan. Ketik harga beli per pcs langsung di tabel; tersimpan saat pindah kolom / Enter.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1080px] [&_td]:px-2 [&_th]:px-2 [&_th]:whitespace-nowrap" data-testid="tabel-koleksi">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-mocha">
              <th className="py-2">Busana</th><th>Kategori</th><th className="text-right">Stok</th><th className="text-right">Disewa</th>
              <th className="text-right">Per stok</th><th className="text-right">Pendapatan</th><th className="text-right">Harga beli / pcs</th>
              <th className="text-right">Modal total</th><th className="text-right">Balik modal</th><th>Terakhir</th><th>Status</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => {
                const st = STATUS_KOLEKSI[r.status];
                return (
                  <tr key={r.code} className="border-t border-border align-middle" data-testid={`baris-${r.code}`}>
                    <td className="py-2"><b className="text-brown-dark">{r.code}</b> <span className="text-mocha">{r.name}</span>
                      {r.baru && <span className="ml-1 rounded-full bg-ivory px-2 py-0.5 text-[10px] font-semibold text-mocha">Baru</span>}</td>
                    <td className="text-mocha">{r.category}</td>
                    <td className="text-right">{r.stok}</td>
                    <td className="text-right">{r.pcs} pcs<span className="block text-[10px] text-mocha">{r.order} order</span></td>
                    <td className="text-right">{kaliModal(r.per_stok)}</td>
                    <td className="text-right">{r.ikut_atasan ? <span className="text-xs text-mocha">ikut atasan</span> : formatIDR(r.pendapatan)}</td>
                    <td className="text-right">
                      <MoneyInput data-testid={`modal-${r.code}`} value={edit[r.code] ?? (r.modal_per_pcs || '')}
                        onChange={(v) => setEdit((e) => ({ ...e, [r.code]: v }))}
                        onBlur={() => simpanModal(r)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        placeholder="isi"
                        className="w-28 rounded-lg border border-border px-2 py-1 text-right text-sm bg-[#FFF7E0]" />
                    </td>
                    <td className="text-right">{r.modal_total ? formatIDR(r.modal_total) : '—'}</td>
                    <td className="text-right font-semibold" data-testid={`balik-${r.code}`}>{r.ikut_atasan ? '—' : kaliModal(r.balik_modal)}</td>
                    <td className="text-xs text-mocha whitespace-nowrap">{r.sewa_terakhir ? `${formatDate(r.sewa_terakhir)} (${r.hari_sejak_sewa} hr)` : '—'}</td>
                    <td><span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap ${st.cls}`}>{st.l}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ImporDialog hasil={impor} onClose={() => setImpor(null)} onApply={terapkan} sibuk={sibuk} />
    </div>
  );
}
